import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { 
  Building2, 
  User, 
  MapPin, 
  FileText,
  CheckCircle2,
  ArrowLeft,
  Briefcase
} from "lucide-react";
import api from "../lib/api";

export default function PublicAgentRegistrationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    // Tipo Agente
    agent_type: "private", // "private" o "company"
    // Dati Personali
    name: "",
    surname: "",
    fiscal_code: "",
    birth_date: "",
    birth_place: "",
    // Contatti
    email: "",
    phone: "",
    mobile: "",
    pec: "",
    // Dati Fiscali (opzionali per privati)
    vat_number: "",
    sdi_code: "",
    enasarco_number: "",
    // Indirizzo
    address: "",
    city: "",
    region: "",
    postal_code: "",
    country: "Italia",
    // Account
    password: "",
    // Note
    notes: "",
    experience: "",
    areas: "", // Zone di competenza
  });

  const isPrivate = formData.agent_type === "private";

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      toast.error("Inserisci il nome");
      return false;
    }
    if (!formData.surname.trim()) {
      toast.error("Inserisci il cognome");
      return false;
    }
    if (!formData.fiscal_code.trim()) {
      toast.error("Inserisci il codice fiscale");
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast.error("Inserisci un'email valida");
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error("Inserisci il telefono");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Per le aziende, i dati fiscali sono obbligatori
    if (!isPrivate) {
      if (!formData.vat_number.trim()) {
        toast.error("Inserisci la Partita IVA");
        return false;
      }
      if (!formData.pec.trim() || !formData.pec.includes("@")) {
        toast.error("Inserisci una PEC valida");
        return false;
      }
      if (!formData.sdi_code.trim()) {
        toast.error("Inserisci il Codice SDI");
        return false;
      }
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.address.trim()) {
      toast.error("Inserisci l'indirizzo");
      return false;
    }
    if (!formData.city.trim()) {
      toast.error("Inserisci la città");
      return false;
    }
    if (!formData.postal_code.trim()) {
      toast.error("Inserisci il CAP");
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      toast.error("La password deve essere di almeno 8 caratteri");
      return false;
    }
    return true;
  };

  const handleNextStep = (nextStep) => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep(nextStep);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setIsSubmitting(true);
    try {
      await api.post("/public/register-agent", formData);
      setIsSubmitted(true);
      toast.success("Registrazione inviata con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Registrazione Ricevuta!</h2>
            <p className="text-muted-foreground mb-6">
              La tua richiesta di registrazione come agente è stata inviata con successo.
              Riceverai una email di conferma una volta che il tuo account sarà stato approvato.
            </p>
            <Button onClick={() => navigate("/login")} className="bg-accent">
              Torna al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/login")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Login
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Registrazione Agente - Lacinio.net</h1>
          <p className="text-muted-foreground mt-2">
            Compila il modulo per richiedere l'accesso come agente commerciale
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? "bg-accent text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 mx-1 ${step > s ? "bg-accent" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Dati Personali e Contatti */}
          <div className={step === 1 ? "space-y-6" : "hidden"}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Dati Personali
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input 
                    required 
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Mario"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input 
                    required 
                    value={formData.surname}
                    onChange={(e) => handleChange("surname", e.target.value)}
                    placeholder="Rossi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Codice Fiscale *</Label>
                  <Input 
                    required 
                    value={formData.fiscal_code}
                    onChange={(e) => handleChange("fiscal_code", e.target.value.toUpperCase())}
                    placeholder="RSSMRA80A01H501Z"
                    maxLength={16}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data di Nascita</Label>
                  <Input 
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => handleChange("birth_date", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Luogo di Nascita</Label>
                  <Input 
                    value={formData.birth_place}
                    onChange={(e) => handleChange("birth_place", e.target.value)}
                    placeholder="Roma (RM)"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-accent" />
                  Contatti
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input 
                    required 
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="mario.rossi@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono *</Label>
                  <Input 
                    required 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="06 1234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cellulare</Label>
                  <Input 
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleChange("mobile", e.target.value)}
                    placeholder="333 1234567"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button type="button" onClick={() => handleNextStep(2)}>
                Avanti
              </Button>
            </div>
          </div>

          {/* Step 2: Dati Fiscali */}
          <div className={step === 2 ? "space-y-6" : "hidden"}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-accent" />
                  Tipologia Agente
                </CardTitle>
                <CardDescription>
                  Seleziona se operi come privato o come azienda
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.agent_type}
                  onValueChange={(value) => handleChange("agent_type", value)}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <div className={`flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-colors ${formData.agent_type === 'private' ? 'border-accent bg-accent/5' : 'border-slate-200 hover:border-slate-300'}`}>
                    <RadioGroupItem value="private" id="private" />
                    <Label htmlFor="private" className="cursor-pointer flex items-center gap-2">
                      <User className="w-5 h-5" />
                      <div>
                        <div className="font-medium">Privato</div>
                        <div className="text-xs text-muted-foreground">Persona fisica senza P.IVA</div>
                      </div>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-colors ${formData.agent_type === 'company' ? 'border-accent bg-accent/5' : 'border-slate-200 hover:border-slate-300'}`}>
                    <RadioGroupItem value="company" id="company" />
                    <Label htmlFor="company" className="cursor-pointer flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      <div>
                        <div className="font-medium">Azienda / Libero Professionista</div>
                        <div className="text-xs text-muted-foreground">Con Partita IVA</div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Dati Fiscali - Solo per Azienda */}
            {!isPrivate && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-accent" />
                    Dati Fiscali e Fatturazione
                  </CardTitle>
                  <CardDescription>
                    Informazioni necessarie per la fatturazione delle provvigioni
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Partita IVA *</Label>
                    <Input 
                      required 
                      value={formData.vat_number}
                      onChange={(e) => handleChange("vat_number", e.target.value)}
                      placeholder="IT00000000000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice SDI *</Label>
                    <Input 
                      required 
                      value={formData.sdi_code}
                      onChange={(e) => handleChange("sdi_code", e.target.value.toUpperCase())}
                      placeholder="XXXXXXX"
                      maxLength={7}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>PEC *</Label>
                    <Input 
                      required 
                      type="email"
                      value={formData.pec}
                      onChange={(e) => handleChange("pec", e.target.value)}
                      placeholder="mario.rossi@pec.it"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>Numero Iscrizione ENASARCO</Label>
                    <Input 
                      value={formData.enasarco_number}
                      onChange={(e) => handleChange("enasarco_number", e.target.value)}
                      placeholder="Numero iscrizione ENASARCO"
                    />
                    <p className="text-xs text-muted-foreground">
                      Se non ancora iscritto, lasciare vuoto
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Messaggio per Privati */}
            {isPrivate && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-800">
                    <strong>Nota per agenti privati:</strong> Non è necessario fornire dati fiscali aziendali. 
                    Le eventuali provvigioni saranno gestite con ritenuta d'acconto.
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setStep(1)}>
                Indietro
              </Button>
              <Button type="button" onClick={() => handleNextStep(3)}>
                Avanti
              </Button>
            </div>
          </div>

          {/* Step 3: Indirizzo e Account */}
          <div className={step === 3 ? "space-y-6" : "hidden"}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  Indirizzo di Residenza/Domicilio
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Indirizzo *</Label>
                  <Input 
                    required 
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    placeholder="Via Roma 123"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Città *</Label>
                  <Input 
                    required 
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="Roma"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>CAP *</Label>
                    <Input 
                      required 
                      value={formData.postal_code}
                      onChange={(e) => handleChange("postal_code", e.target.value)}
                      placeholder="00100"
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input 
                      value={formData.region}
                      onChange={(e) => handleChange("region", e.target.value.toUpperCase())}
                      placeholder="RM"
                      maxLength={2}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-accent" />
                  Esperienza e Zone
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Zone di Competenza (Regioni e Province)</Label>
                  <Textarea 
                    value={formData.areas}
                    onChange={(e) => handleChange("areas", e.target.value)}
                    placeholder="Es. Calabria (CS, CZ, KR, RC, VV), Basilicata (PZ, MT)"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Indica le regioni e le relative province di competenza
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Esperienza nel Settore</Label>
                  <Textarea 
                    value={formData.experience}
                    onChange={(e) => handleChange("experience", e.target.value)}
                    placeholder="Descrivi brevemente la tua esperienza nel settore..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Note Aggiuntive</Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    placeholder="Altre informazioni utili..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Credenziali di Accesso
                </CardTitle>
                <CardDescription>
                  Queste credenziali saranno utilizzate per accedere alla piattaforma una volta approvata la registrazione
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email di Accesso</Label>
                  <Input 
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Utilizzerai questa email per accedere
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input 
                    required 
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    placeholder="Minimo 8 caratteri"
                    minLength={8}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setStep(2)}>
                Indietro
              </Button>
              <Button type="submit" className="bg-accent" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Invio in corso...
                  </span>
                ) : (
                  "Invia Registrazione"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
